'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Star, ChevronLeft, ChevronRight, Quote, CheckCircle } from 'lucide-react';

const TESTIMONIALS = [
{
  id: 'review-001',
  name: 'Priya Sharma',
  location: 'Delhi',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19205d2aa-1763296356182.png",
  avatarAlt: 'Indian woman smiling in professional setting with warm background',
  rating: 5,
  trip: 'Delhi → Shimla → Manali (7 Days)',
  review:
  'GhumoJi Travels made our family trip absolutely memorable! The driver Ramesh ji was extremely professional and knew every road in Himachal. The fare was exactly what was quoted — no surprises at all. Will definitely book again for our Kashmir trip.',
  verified: true,
  date: '18 Jul 2026'
},
{
  id: 'review-002',
  name: 'Arjun Mehta',
  location: 'Chandigarh',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5ea51b0-1763293852695.png",
  avatarAlt: 'Young Indian man in casual shirt smiling outdoors',
  rating: 5,
  trip: 'Spiti Valley Circuit (10 Days)',
  review:
  'Booked the Spiti Valley package for our group of 8. The Tempo Traveller was in excellent condition and our driver Suresh bhai handled the rough terrain like a pro. The itinerary was perfectly planned. Highly recommend for adventure seekers!',
  verified: true,
  date: '02 Aug 2026'
},
{
  id: 'review-003',
  name: 'Kavita Nair',
  location: 'Pune',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14cc99f8b-1772266055502.png",
  avatarAlt: 'South Indian woman with long hair smiling in garden setting',
  rating: 5,
  trip: 'Kashmir Paradise Tour (6 Days)',
  review:
  'The Kashmir tour package was beyond our expectations. Houseboat stay on Dal Lake, Gulmarg gondola, Betaab Valley — everything was perfectly organized. The GhumoJi team was responsive on WhatsApp throughout. Absolutely 5 stars!',
  verified: true,
  date: '25 Jul 2026'
},
{
  id: 'review-004',
  name: 'Rajesh Gupta',
  location: 'Jaipur',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb468779-1763295443265.png",
  avatarAlt: 'Middle-aged Indian man with glasses in business casual attire',
  rating: 4,
  trip: 'Delhi → Jaipur Corporate Transfer',
  review:
  'Used GhumoJi for a corporate trip from Delhi to Jaipur. Clean Innova, punctual driver, GST invoice provided immediately. Exactly what corporate travel needs. Will use for all our company trips going forward.',
  verified: true,
  date: '30 Jul 2026'
},
{
  id: 'review-005',
  name: 'Ananya Singh',
  location: 'Lucknow',
  avatar: "https://images.unsplash.com/photo-1544202262-dc75a477f1a5",
  avatarAlt: 'Young Indian woman in traditional attire smiling in natural light',
  rating: 5,
  trip: 'Char Dham Yatra by Tempo Traveller',
  review:
  'Did the Char Dham Yatra with 12 family members including elderly parents. The Tempo Traveller was comfortable and spacious. Driver Mohan ji was extremely patient and helpful during the yatra. GhumoJi arranged everything flawlessly.',
  verified: true,
  date: '10 Jul 2026'
}];


export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const total = TESTIMONIALS?.length;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  const visible = [
  TESTIMONIALS?.[current],
  TESTIMONIALS?.[(current + 1) % total],
  TESTIMONIALS?.[(current + 2) % total]];


  return (
    <section className="py-16 lg:py-20 bg-primary overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        <div className="text-center mb-10">
          <p className="text-secondary text-sm font-semibold uppercase tracking-wider mb-2">
            Traveler Stories
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            What Our Customers Say
          </h2>
          <p className="text-white/65 mt-3 max-w-xl mx-auto text-base">
            Real experiences from real travelers. 8,500+ verified reviews and counting.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {visible?.map((review, i) =>
          <div
            key={`${review?.id}-slot-${i}`}
            className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/15 transition-all duration-300 ${
            i === 0 ? 'opacity-100 scale-100' : 'opacity-80 scale-98'}`
            }>
            
              <Quote size={28} className="text-accent mb-3" />
              <p className="text-white/85 text-sm leading-relaxed mb-4 line-clamp-4">
                {review?.review}
              </p>

              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 })?.map((_, si) =>
              <Star
                key={`${review?.id}-star-${si}`}
                size={14}
                className={si < review?.rating ? 'text-accent fill-current' : 'text-white/30'} />

              )}
              </div>

              <div className="text-xs text-white/55 mb-4 flex items-center gap-1">
                <span className="font-medium text-accent/80">{review?.trip}</span>
              </div>

              <div className="flex items-center gap-3 pt-3 border-t border-white/15">
                <div className="relative">
                  <AppImage
                  src={review?.avatar}
                  alt={review?.avatarAlt}
                  width={42}
                  height={42}
                  className="w-10 h-10 rounded-full object-cover border-2 border-accent" />
                
                  {review?.verified &&
                <CheckCircle
                  size={14}
                  className="absolute -bottom-0.5 -right-0.5 text-secondary fill-white" />

                }
                </div>
                <div>
                  <div className="font-semibold text-white text-sm">{review?.name}</div>
                  <div className="text-white/50 text-xs">
                    {review?.location} · {review?.date}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prev}
            className="w-10 h-10 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-all duration-150 hover:scale-105"
            aria-label="Previous testimonial">
            
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            {TESTIMONIALS?.map((_, i) =>
            <button
              key={`dot-${i}`}
              onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-200 ${
              i === current ? 'w-6 h-2.5 bg-accent' : 'w-2.5 h-2.5 bg-white/30 hover:bg-white/50'}`
              }
              aria-label={`Go to testimonial ${i + 1}`} />

            )}
          </div>
          <button
            onClick={next}
            className="w-10 h-10 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center text-white transition-all duration-150 hover:scale-105"
            aria-label="Next testimonial">
            
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>);

}